import { Component } from '@angular/core';
import {  GlobalService } from 'src/app/core/services'
import { FormControl, FormGroup, Validators, ValidationErrors, AbstractControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { defaultLogo, defaultLoginBackground, pattern, appStoreIcon, playStoreIcon, neoPatIcon } from '@assets';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/core/services';
import { emailRegex } from 'src/app/core/services';

type TabTypes = 'login' | 'reset' | 'change';

type SubTitleMessages = {
  [key in TabTypes]: { title: string; subTitle: string };
};

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent {
  isLoading: boolean = false;
  logo: string = neoPatIcon;
  errorMessage: string = '';
  emailPattern: RegExp = emailRegex;
  loginForm: FormGroup;
  invalidCred: boolean = false;
  currentTab: TabTypes  = 'login';
  showSuccessGrowl = false;
  isSuccess = true;
  successMail: string = '';
  defaultLogo = defaultLogo;
  defaultLoginBackground = defaultLoginBackground;
  pattern = pattern;
  assets_bucket: string = '';
  showPassword: boolean = false;

  subTitleMessages: SubTitleMessages = {
    login: {
      title: 'Sign in',
      subTitle: 'The key to happiness is to sign in.'
    },
    reset: {
      title: 'Forgot Password',
      subTitle: 'Enter your email address and we will send you a link to reset password.'
    },
    change: {
      title: 'Forgot Password',
      subTitle: 'You can create new password below. '
    }
  };
  title: string;
  schoolName: string = '';
  isLoadingPage: boolean = true;
  isEmailEntered: boolean = false;
  appStoreIcon = appStoreIcon;
  appStoreLink = 'https://apps.apple.com/in/app/neo-pat/id6477999254';
  playStoreIcon = playStoreIcon;
  playStoreLink = 'https://play.google.com/store/apps/details?id=com.iamneo.drive&hl=en_IN';
  captchaKey = environment.captchaKey;
  encodedUrl: string | null = null;

  constructor(
      public globalService: GlobalService, 
      private authService: AuthService, 
      private router: Router , 
      private activatedRoute: ActivatedRoute,
      private location: Location
    ) {
    if (this.globalService.isAuthenticated()) {
      this.router.navigate(['/drive-planner']);
    }

    this.loginForm = new FormGroup({
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(this.emailPattern),
      ]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
      ]),    
    });

    const accountData = JSON.parse(localStorage.getItem('account_details') ?? '{}');
    this.title = accountData.school_name;
    this.assets_bucket = this.globalService.bucketName;

    if(accountData?.PWA) {
      this.globalService.setTitle(accountData.PWA);
    };
    

    if(this.location.path().includes('forgot')) {
        this.activatedRoute.queryParams.subscribe(async params => {
          try {

            if(!params['key']) {
              this.router.navigate(['/login']);
              return;
            };
            this.encodedUrl = params['key'];

            const payload = {
              encodedUrl: this.encodedUrl
            };
            
            const response = await this.authService.requestVerify(payload);
  
            if (!response.success || response.data === 'Invalid Link') {
              this.globalService.tokenValidationMessage({ message: 'Invalid Link' });
              this.router.navigate(['/login']);
              return;
            }
  
            if (response.data === 'Link expired') {
              this.globalService.tokenValidationMessage({ message: 'Link expired' });
              this.router.navigate(['/login']);
              return;
            }
            
            this.loginForm.get('email')?.setValue(response.data.email);
            this.switchTab('change');
          } 
          catch (error) {
            console.error('Error during request verification:', error);
          }
        });
    };
  };

  async ngOnInit() {
    this.isLoadingPage = false;
    this.globalService.accountDetailSubject.subscribe((data)=>
    {
      if(data?.logo) {
        this.logo = data.logo || neoPatIcon;
      }

      if(data.group_account_id) {
        this.schoolName = data.school_name;
        if (this.logo && this.schoolName) {
          this.isLoadingPage = false;
        }
      }
    })
  };

  ngAfterViewInit() {
    this.loadTurnstileScript();
  };

  async loadTurnstileScript() {
    const turnstileDiv = document.createElement('div');
    turnstileDiv.className = 'cf-turnstile';
    turnstileDiv.setAttribute('data-sitekey', this.captchaKey);
    const turnstileWidget = document.querySelector('#turnstileWidget');
    turnstileWidget?.appendChild(turnstileDiv);

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject('Turnstile script failed to load.');
      };
      document.body.appendChild(script);
    });
  };

  async getAccountDataWithEmail() {
    this.loginForm.get('email')?.markAsTouched();

    if (!this.loginForm.get('email')?.valid) {
      return;
    };

    this.isLoading = true;

    const payload = {
      email: this.loginForm.get('email')?.value
    };

    const response = await this.authService.accountsLookup(payload);
    this.isLoading = false;
    
    if(!response.data || !response.success) {
      this.invalidCred = true;
      this.errorMessage = 'Account not found';
      return;
    };

    this.isEmailEntered = true;

    // setting account details in cookie
    this.globalService.setCookie('account_details', JSON.stringify(response.data));
    this.globalService.accountDetailSubject.next(response.data);
  };

  async signIn() {
    this.loginForm.get('email')?.markAsTouched();
    this.loginForm.get('password')?.markAsTouched();

    if (!this.loginForm.valid) {
      return;
    };

    const accountData = JSON.parse(this.globalService.getCookie('account_details'));
    if(!accountData) {
      return this.globalService.tokenValidationMessage({ message: 'Account not found' });
    };
  
    this.isLoading = true;
    const turnstileToken = await this.getTurnSiteToken();

    if (!turnstileToken && accountData.domain_name !== environment.skip_captcha_domain) {
      this.isLoading = false;
      return this.globalService.tokenValidationMessage({ message: 'Invalid Captcha Token, Please try again' });
    };
  
    const payload = {
      email: this.loginForm.value.email,
      password: this.loginForm.value.password,
      group_account_id: accountData.group_account_id,
      domain_name: accountData.domain_name,
      turnstileToken: turnstileToken
    };
  
    try {
      const response = await this.authService.login(payload);
      if (response.success && response.data) {
        // setting cookie before login to redirect to correct subdomain
        this.globalService.setCookie('token', response.data["token"]);
        this.globalService.setCookie('refresh_token', response.data["refresh_token"]);
        this.globalService.setCookie('user_details', JSON.stringify(response.data["data"]));
        this.globalService.setCookie('subdomain', accountData.domain_name);

        // redirecting to subdomain
        window.location.href = this.globalService.redirectUrl(accountData.domain_name, 'drive-planner');
      } else if (response.message === 'invalid captcha') {
        this.invalidCred = true;
        this.errorMessage = 'Invalid Captcha Token';
      }
      else {
        this.invalidCred = true;
        this.errorMessage = 'Account and Password do not match';
      }
    } catch (error) {
      console.error('Error during login:', error);
      this.errorMessage = 'An error occurred during login. Please try again later.';
    } finally {
      this.isLoading = false;
    }
  };

  async getTurnSiteToken() {
    for (let attempt = 0; attempt < 10; attempt++) {
      const turnstileToken = (window as any).turnstile.getResponse();

      if (turnstileToken) {
        return turnstileToken;
      }

      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait before retrying
    }

    return null;
  };

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
  
    return newPassword && confirmPassword && newPassword === confirmPassword ? null : { 'passwordMismatch': true };
  }

  switchTab(page: TabTypes) {
    if(page == 'reset') {
      this.loginForm.get('email')?.reset('');
      this.loginForm.get('email')?.markAsUntouched();
      this.loginForm.removeControl('password');
    }
    else if(page == 'login') {
      this.loginForm.removeControl('newPassword');
      this.loginForm.removeControl('confirmPassword');
      this.loginForm.get('email')?.setValue('');
      this.loginForm.get('email')?.markAsUntouched();
      this.isEmailEntered = false;

      const passwordControl = new FormControl('', [
        Validators.required,
        Validators.minLength(1)
      ]);
    
      this.loginForm.addControl('password', passwordControl);
    }
    else {
      this.loginForm.removeControl('password');

      this.loginForm.get('email')?.markAllAsTouched();
      this.loginForm.get('email')?.disable();

      const newPasswordControl = new FormControl('', [ Validators.required ]);
      const confirmPasswordControl = new FormControl('', [
        Validators.required
      ]);

      this.loginForm.setValidators(this.passwordMatchValidator.bind(this));
      this.loginForm.addControl('newPassword', newPasswordControl);
      this.loginForm.addControl('confirmPassword', confirmPasswordControl);
    }

    this.currentTab = page;
    this.showSuccessGrowl = false;
    this.successMail = '';
  };

  async resetPassword() {
    if(!this.loginForm.valid) return;
    this.isLoading = true;

    const payload = {
      email: this.loginForm.get('email')?.value
    };

    const response = await this.authService.forgotPassword(payload);
    this.isLoading = false;
    this.showSuccessGrowl = true;
    this.isSuccess = response.data;
    this.successMail = payload.email;
  };

  async changePassword() {
    if(!this.loginForm.valid) {
      this.loginForm.get('newPassword')?.markAllAsTouched();
      this.loginForm.get('confirmPassword')?.markAllAsTouched();
      return;
    };

    this.isLoading = true;

    const turnstileToken = await this.getTurnSiteToken();

    const payload = {
      encodedUrl: this.encodedUrl,
      password: this.loginForm.get('newPassword')?.value,
      turnstileToken
    }

    const response = await this.authService.changePassword(payload);
    this.isLoading = false;
    
    if(!response.success) {
      return this.globalService.tokenValidationMessage({ message: 'Something went wrong' });
    };

    if(response.data == 'same password') {
      return this.globalService.tokenValidationMessage({ message: 'Old Password cannot be a new Password' });
    };

    if(response.data == 'invalid captcha') {
      return this.globalService.tokenValidationMessage({ message: 'Invalid Captcha Token' });
    };

    this.router.navigate(['/login'])
  }

  imageLoadError(event: any){
    event.target.src = defaultLoginBackground;
  }

  openLink(link: string) {
    window.open(link, '_blank');
  };

  resetEmailEntered(){
    this.loginForm.get('email')?.reset('');
    this.loginForm.get('email')?.markAsUntouched();
    this.loginForm.get('password')?.reset('');
    this.loginForm.get('password')?.markAsUntouched();
    this.isEmailEntered = false;  
    this.logo = neoPatIcon;
    this.schoolName = '';
    this.globalService.deleteCookie('account_details');
    this.showPassword = false;
  }
  
  showAction(){
    this.showPassword = !this.showPassword;
  }
}
